import React, { useMemo, useState, useCallback, useEffect } from "react";
import SectionRenderer from "../machineWidgets/SectionRenderer";

/**
 * Extract all source_keys from config (mirrors backend extractSourceKeys)
 */
function extractSourceKeysFromConfig(config) {
  const keys = new Set();
  if (!config?.sections) return keys;

  config.sections.forEach(section => {
    // Section-level detailAction (update action)
    if (section.detailAction?.source_key) {
      keys.add(section.detailAction.source_key);
    }

    section.widgets?.forEach(widget => {
      widget.items?.forEach(item => {
        if (item.source_key) keys.add(item.source_key);
        if (item.onClick?.source_key) keys.add(item.onClick.source_key);
      });
      const opts = widget.options || {};
      if (opts.source_key) keys.add(opts.source_key);
      if (opts.dataKey) keys.add(opts.dataKey);
      // Widget-level detailAction
      const wAction = widget.detailAction || opts.detailAction;
      if (wAction?.source_key) keys.add(wAction.source_key);
      // Reference lines
      opts.referenceLines?.forEach(ref => {
        if (ref.type === 'source' && ref.source_key) keys.add(ref.source_key);
      });
    });
  });

  return keys;
}

/**
 * Generate mock data from config for preview
 */
function generateMockData(config) {
  const mockData = {};
  if (!config?.sections) return mockData;

  // Helper to assign mock value based on key name
  const assignMock = (key) => {
    if (!key || mockData[key] !== undefined) return;
    const lk = key.toLowerCase();
    if (lk.includes('count') || lk.includes('건')) mockData[key] = 5;
    else if (lk.includes('cycle') || lk.includes('주기')) mockData[key] = 10000;
    else if (lk.includes('rate') || lk.includes('compliance') || lk.includes('율') || lk.includes('life')) mockData[key] = 75;
    else if (lk.includes('date') || lk.includes('일')) mockData[key] = '2025-01-15';
    else if (lk.includes('name') || lk.includes('명')) mockData[key] = '샘플 데이터';
    else if (lk.includes('status') || lk.includes('상태')) mockData[key] = '정상';
    else mockData[key] = 100;
  };

  config.sections.forEach(section => {
    // Mock for section detailAction source_key
    if (section.detailAction?.source_key) {
      assignMock(section.detailAction.source_key);
    }

    section.widgets?.forEach(widget => {
      widget.items?.forEach(item => {
        const key = item.source_key || item.dataKey;
        if (!key) return;
        const lk = key.toLowerCase();
        if (lk.includes('count') || lk.includes('건')) {
          mockData[key] = 5;
        } else if (lk.includes('rate') || lk.includes('compliance') || lk.includes('율')) {
          mockData[key] = 75;
        } else if (lk.includes('date') || lk.includes('일')) {
          mockData[key] = '2025-01-15';
        } else if (lk.includes('name') || lk.includes('명')) {
          mockData[key] = '샘플 데이터';
        } else if (lk.includes('status') || lk.includes('상태')) {
          mockData[key] = '정상';
        } else {
          mockData[key] = '—';
        }
      });

      const opts = widget.options || {};
      const optKey = opts.source_key || opts.dataKey;
      if (optKey) {
        const lk = optKey.toLowerCase();
        if (lk.includes('progress') || lk.includes('율') || lk.includes('compliance')) {
          mockData[optKey] = 68;
        } else if (lk.includes('date') || lk.includes('일')) {
          mockData[optKey] = '2025-03-20';
        } else if (lk.includes('table') || lk.includes('list') || lk.includes('log') || lk.includes('event')) {
          mockData[optKey] = [
            { date: '2025-01-01', title: '샘플 이벤트 1', description: '설명입니다', status: '완료', task_name: '작업 A' },
            { date: '2025-01-05', title: '샘플 이벤트 2', description: '설명입니다', status: '진행중', task_name: '작업 B' },
            { date: '2025-01-10', title: '샘플 이벤트 3', description: '설명입니다', status: '대기', task_name: '작업 C' },
          ];
        } else {
          mockData[optKey] = '샘플';
        }
      }
    });
  });

  return mockData;
}

/**
 * Preview Panel
 * Right panel showing live preview with SectionRenderer
 * Supports mock mode and live data connection check
 */
export default function PreviewPanel({ parsedConfig, isJsonInvalid, dataSources, axiosPrivate, groupCode, projectCode }) {
  const [previewMode, setPreviewMode] = useState('mock'); // 'mock' | 'live'
  const [liveData, setLiveData] = useState(null);
  const [isLoadingLive, setIsLoadingLive] = useState(false);
  const [testMchId, setTestMchId] = useState('');
  const [liveError, setLiveError] = useState(null);

  const mockData = useMemo(() => generateMockData(parsedConfig), [parsedConfig]);

  // Extract source_keys used in current config
  const usedSourceKeys = useMemo(() => extractSourceKeysFromConfig(parsedConfig), [parsedConfig]);

  // Build connection status: which source_keys exist in dataSources
  const connectionStatus = useMemo(() => {
    if (!dataSources || usedSourceKeys.size === 0) return [];
    
    const dsMap = new Map(dataSources.map(ds => [ds.source_key, ds]));
    
    return Array.from(usedSourceKeys).map(key => {
      const ds = dsMap.get(key);
      return {
        key,
        connected: !!ds,
        source: ds || null,
        hasData: liveData ? (liveData[key] !== undefined && liveData[key] !== null) : null,
      };
    });
  }, [usedSourceKeys, dataSources, liveData]);

  // Fetch live data for testing
  const handleFetchLiveData = useCallback(async () => {
    if (!testMchId || !axiosPrivate || !groupCode || !projectCode) return;
    
    setIsLoadingLive(true);
    setLiveError(null);
    try {
      const response = await axiosPrivate.post('/dt/mchinfomod/full', {
        mchId: testMchId,
        group_code: groupCode,
        project_code: projectCode,
      });
      setLiveData(response.data?.data || {});
    } catch (err) {
      console.error('Live preview error:', err);
      setLiveError(err.response?.data || '데이터 조회 실패');
      setLiveData(null);
    } finally {
      setIsLoadingLive(false);
    }
  }, [testMchId, axiosPrivate, groupCode, projectCode]);

  // Handle update action from preview (live mode only)
  const handlePreviewAction = useCallback(async (actionType, actionData) => {
    if (actionType === 'updateValue' && previewMode === 'live' && testMchId && axiosPrivate) {
      try {
        await axiosPrivate.post('/dt/mchinfomod/update-value', {
          source_key: actionData.source_key,
          dataKey: actionData.dataKey,
          value: actionData.value,
          mchId: testMchId,
          group_code: groupCode,
          project_code: projectCode,
        });
        // Re-fetch live data to show updated values
        await handleFetchLiveData();
      } catch (err) {
        console.error('Preview update error:', err);
        setLiveError(err.response?.data?.message || '값 업데이트 실패');
        throw err;
      }
    } else if (actionType === 'updateValue' && previewMode === 'mock') {
      // In mock mode, just update mock data locally for demo
      console.log('[Preview Mock] updateValue:', actionData);
    }
  }, [previewMode, testMchId, axiosPrivate, groupCode, projectCode, handleFetchLiveData]);

  // Reset live data when switching to mock
  useEffect(() => {
    if (previewMode === 'mock') {
      setLiveData(null);
      setLiveError(null);
    }
  }, [previewMode]);

  const displayData = previewMode === 'live' && liveData ? liveData : mockData;

  const connectedCount = connectionStatus.filter(c => c.connected).length;
  const totalCount = connectionStatus.length;

  return (
    <div className="w-[380px] flex-shrink-0 bg-slate-900 flex flex-col">
      {/* Header with mode toggle */}
      <div className="px-4 py-3 border-b border-slate-800">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-black text-slate-300 uppercase tracking-tight">
            미리보기
          </h2>
          <div className="flex bg-slate-800 rounded-lg p-0.5">
            <button
              onClick={() => setPreviewMode('mock')}
              className={`px-2.5 py-1 text-[10px] font-bold rounded-md transition-colors ${
                previewMode === 'mock'
                  ? 'bg-slate-700 text-slate-200'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              목업
            </button>
            <button
              onClick={() => setPreviewMode('live')}
              className={`px-2.5 py-1 text-[10px] font-bold rounded-md transition-colors ${
                previewMode === 'live'
                  ? 'bg-indigo-500/30 text-indigo-400'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              실 데이터
            </button>
          </div>
        </div>

        {/* Connection status summary */}
        {totalCount > 0 && (
          <div className="flex items-center gap-2 text-[10px]">
            <span className={`font-bold ${connectedCount === totalCount ? 'text-emerald-400' : 'text-orange-400'}`}>
              소스 연결: {connectedCount}/{totalCount}
            </span>
            {connectionStatus.some(c => !c.connected) && (
              <span className="text-red-400">
                ({connectionStatus.filter(c => !c.connected).map(c => c.key).join(', ')} 미등록)
              </span>
            )}
          </div>
        )}

        {/* Live data: mchId input */}
        {previewMode === 'live' && (
          <div className="mt-2 flex gap-1">
            <input
              type="text"
              value={testMchId}
              onChange={(e) => setTestMchId(e.target.value)}
              placeholder="장비 mchId 입력 (dt_sync_id)"
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1 text-xs text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <button
              onClick={handleFetchLiveData}
              disabled={!testMchId || isLoadingLive}
              className={`px-3 py-1 text-xs font-bold rounded-lg transition-colors ${
                !testMchId || isLoadingLive
                  ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-500 text-white'
              }`}
            >
              {isLoadingLive ? '...' : '조회'}
            </button>
          </div>
        )}
      </div>

      {/* Connection detail panel (collapsible) */}
      {connectionStatus.length > 0 && (
        <details className="border-b border-slate-800">
          <summary className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase cursor-pointer hover:text-slate-300 transition-colors">
            소스 연결 상세 ({connectedCount}/{totalCount})
          </summary>
          <div className="px-4 pb-3 space-y-1 max-h-40 overflow-y-auto">
            {connectionStatus.map(({ key, connected, source, hasData }) => (
              <div key={key} className="flex items-center gap-2 text-[10px]">
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  connected 
                    ? (hasData !== null ? (hasData ? 'bg-emerald-400' : 'bg-orange-400') : 'bg-indigo-400') 
                    : 'bg-red-400'
                }`} />
                <code className="font-mono text-slate-400 flex-1">{key}</code>
                {connected ? (
                  <span className="text-emerald-400/70">
                    {source?.source_type}
                    {hasData !== null && (hasData ? ' ✓' : ' (빈값)')}
                  </span>
                ) : (
                  <span className="text-red-400/70">미등록</span>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Preview content */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 pb-16 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
        {liveError && (
          <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">
            {typeof liveError === 'string' ? liveError : JSON.stringify(liveError)}
          </div>
        )}

        {isJsonInvalid ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-red-400">
              <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium">JSON 오류로 미리보기를 표시할 수 없습니다</p>
            </div>
          </div>
        ) : parsedConfig && parsedConfig.sections ? (
          <div className="bg-[#0a0e1a] rounded-xl p-4" style={{ border: '1px solid rgba(255,255,255,0.05)' }}>
            {previewMode === 'live' && !liveData && !isLoadingLive && (
              <div className="text-center py-6 text-slate-500 text-xs">
                장비 ID를 입력하고 조회하면 실제 데이터로 미리보기됩니다
              </div>
            )}
            {isLoadingLive && (
              <div className="text-center py-6">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-indigo-500 border-t-transparent"></div>
                <p className="mt-2 text-xs text-slate-400">데이터 조회 중...</p>
              </div>
            )}
            {(previewMode === 'mock' || liveData) && (
              parsedConfig.sections.map((section, idx) => (
                <SectionRenderer
                  key={idx}
                  section={section}
                  data={displayData}
                  onOpenHistory={() => {}}
                  onAction={handlePreviewAction}
                />
              ))
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-slate-500">
              <svg className="w-16 h-16 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <p className="text-sm">JSON을 입력하면 미리보기가 표시됩니다</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
